import React, {useEffect, useState} from "react"
import {Alert, Autocomplete, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, FormControl, Grid, MenuItem, TextField} from "@mui/material";
import {DialogContainer} from "../../dialogs/components/DialogContainer";
import {useAppSelector} from "../../../app/hooks/useAppSelector";
import {useApiClient} from "../../../shared/api/hooks/useApiClient";
import {DialogCloseButton} from "../../dialogs/components/DialogCloseButton";
import {changeEntries} from "../../../app/reducers/changesReducer";
import {useImportProjectTranslationsDialog} from "../hooks/useImportProjectTranslationsDialog";
import {useAppDispatch} from "../../../app/hooks/useAppDispatch";
import {useAppVersions} from "../../translations/hooks/useAppVersions";
import {KeyOptionValue} from "../../translations/hooks/useKeyOptions";
import {Query} from "../../../shared/utils/query";

export const ImportProjectTranslationsDialog = () => {
    const dispatch = useAppDispatch();
    const context = useImportProjectTranslationsDialog();
    const apiClient = useApiClient();

    const projects = useAppSelector(s => s.settings.user.projects);
    const selectedProject = useAppSelector(s => s.view.project);

    const [sourceProject, setSourceProject] = useState<string>('');
    const [targetProject, setTargetProject] = useState<string>('');
    const [projectVersion, setProjectVersion] = useState<string>('');
    const [request, setRequest] = useState<{ sourceProject: string, targetProject: string, projectVersion: string | undefined }>();
    const [error, setError] = useState<string>();
    const [info, setInfo] = useState<string>();
    const [loading, setLoading] = useState(false);

    const appVersions = useAppVersions(sourceProject);

    const submit = () => {
        sourceProject
        && targetProject
        && setRequest({ sourceProject, targetProject, projectVersion })
    };

    useEffect(() => context.onHideSubscription(() => {
        setInfo(undefined);
        setError(undefined);
        setRequest(undefined);
    }), [context]);

    useEffect(() => {
        setTargetProject(selectedProject)
    }, [selectedProject]);

    useEffect(() => {
        if (!request || loading) {
            return;
        }

        setLoading(true);
        setRequest(undefined);
        setError(undefined);
        setInfo(undefined);

        apiClient.current.importTranslations(request)
            .then(values => {
                if (values?.length) {
                    dispatch(changeEntries({updates: values}));
                    setInfo('Changes have been made');
                } else {
                    setInfo('Nothing to import');
                }
            })
            .catch(e => setError(e.message))
            .finally(() => {
                setLoading(false);
            })
    }, [apiClient, dispatch, loading, request]);
    
    const renderProjectOptions = () => Object.keys(projects)
        .sort((a, b) => projects[a].localeCompare(projects[b]))
        .map(p => <MenuItem value={p} key={p}>{projects[p]}</MenuItem>);

    const renderAlerts = () => (
        <React.Fragment>
            {error && <Grid item><Alert severity="error">{error}</Alert></Grid>}
            {info && <Grid item><Alert severity="info">{info}</Alert></Grid>}
        </React.Fragment>
    )

    const renderLoading = () => (
        <Box sx={{display: 'flex'}}>
            <CircularProgress/>
        </Box>
    )

    return (
        <DialogContainer dialogName={context.dialogName}>
            <DialogTitle>Import Project Translations</DialogTitle>
            <DialogCloseButton dialogName={context.dialogName}/>
            <DialogContent dividers>
                <Grid container direction={"column"} spacing={2}>
                    {renderAlerts()}

                    {loading ?
                        renderLoading()
                        : <React.Fragment>
                            <Grid item>
                                <FormControl fullWidth>
                                    <TextField label="Source Project"
                                               value={sourceProject}
                                               select
                                               onChange={x => setSourceProject(x.target.value)}>
                                        {renderProjectOptions()}
                                    </TextField>
                                </FormControl>
                            </Grid>

                            <Grid item>
                                <FormControl fullWidth>
                                    <TextField label="Target Project"
                                               value={targetProject}
                                               select
                                               disabled={true}
                                               onChange={x => setTargetProject(x.target.value)}>
                                        {renderProjectOptions()}
                                    </TextField>
                                </FormControl>
                            </Grid>

                            <Grid item>
                                <FormControl fullWidth>
                                    <TextField label="Project Version (optional)"
                                               value={projectVersion}
                                               select
                                               disabled={!sourceProject}
                                               helperText="Leave as 'All versions' to import keys from every version"
                                               onChange={x => setProjectVersion(x.target.value)}>
                                        <MenuItem value="" key="__all"><em>All versions</em></MenuItem>
                                        {appVersions.map(v => <MenuItem value={String(v.value)} key={v.value}>{v.label}</MenuItem>)}
                                    </TextField>
                                </FormControl>
                            </Grid>
                        </React.Fragment>}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => submit()} disabled={loading || !sourceProject || !targetProject}>Import</Button>
            </DialogActions>
        </DialogContainer>
    )
}


/*
* 

                    <Autocomplete 
                        renderInput={(params) => 
                            <TextField {...params} 
                                label="Project Version"
                                type="text"
                                size='small'
                                placeholder="Search"
                                variant={"standard"}
                                value={projectVersion}
                                inputProps={{
                                    ...params.inputProps,
                                    enterKeyHint: 'done'
                                }}/>
                        }
                        sx={{
                            '& .MuiFormControl-root': {
                                padding: '2px'
                            }
                        }}
                        onChange={(_, value) => {
                            const text = (value as KeyOptionValue)?.label || value as string;
                            Query.setSearchQuery(text);
                            setProjectVersion(text);
                        }}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        fullWidth
                        ListboxProps={{
                            style: {
                                fontSize: '12px'
                            }
                        }}
                        value={projectVersion}
                        clearOnEscape
                        options={originalAppVersions}
                        freeSolo
                        size={'small'}/>
* 
* */